import { format } from 'date-fns';
import {
  AlertCircle,
  Calendar,
  Lock,
  Mail,
  Phone,
  Shield,
  UserCheck,
} from 'lucide-react';
import { User } from '@/services/admin/user.service';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface UserDetailsOverviewProps {
  user: User;
}

export function UserDetailsOverview({ user }: UserDetailsOverviewProps) {
  const isLocked = user.lockoutEnd && new Date(user.lockoutEnd) > new Date();

  return (
    <div className="space-y-6">
      {/* Status Alerts */}
      {!user.isActive && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            <div>
              <p className="font-medium text-orange-900">Inactive Account</p>
              <p className="text-sm text-orange-700">
                This user account is currently inactive and cannot login.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {isLocked && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Lock className="h-5 w-5 text-red-600" />
            <div>
              <p className="font-medium text-red-900">Account Locked</p>
              <p className="text-sm text-red-700">
                Locked until {format(new Date(user.lockoutEnd!), 'PPP p')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <UserCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Full Name
                </p>
                <p className="font-medium">
                  {user.name || user.userName} {user.surname || ''}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Email
                </p>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{user.email}</p>
                  {user.emailConfirmed ? (
                    <Badge variant="success" className="text-xs">
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      Unverified
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {user.phoneNumber && (
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Phone
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{user.phoneNumber}</p>
                    {user.phoneNumberConfirmed ? (
                      <Badge variant="success" className="text-xs">
                        Verified
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Unverified
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Username
                </p>
                <p className="font-medium">@{user.userName}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Account Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Active Status
              </span>
              {user.isActive ? (
                <Badge variant="success">Active</Badge>
              ) : (
                <Badge variant="secondary">Inactive</Badge>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Lockout Status
              </span>
              {isLocked ? (
                <Badge variant="destructive">Locked</Badge>
              ) : (
                <Badge variant="outline">Unlocked</Badge>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Lockout Enabled
              </span>
              <Badge variant={user.lockoutEnabled ? 'outline' : 'secondary'}>
                {user.lockoutEnabled ? 'Yes' : 'No'}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Failed Attempts
              </span>
              <Badge
                variant={user.accessFailedCount > 0 ? 'destructive' : 'outline'}
              >
                {user.accessFailedCount}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Assigned Roles */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Assigned Roles</CardTitle>
          </CardHeader>
          <CardContent>
            {user.roles && user.roles.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {user.roles.map((role, index) => (
                  <Badge key={index} variant="outline">
                    {role}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No roles assigned</p>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Timeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Created
                </p>
                <p className="font-medium">
                  {format(new Date(user.creationTime), 'PPP p')}
                </p>
              </div>
            </div>

            {user.lastModificationTime && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Last Modified
                  </p>
                  <p className="font-medium">
                    {format(new Date(user.lastModificationTime), 'PPP p')}
                  </p>
                </div>
              </div>
            )}

            {isLocked && user.lockoutEnd && (
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Locked Until
                  </p>
                  <p className="font-medium text-red-600">
                    {format(new Date(user.lockoutEnd), 'PPP p')}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
